import React from 'react';
import { X, TrendingUp, TrendingDown, Clock, Activity as ActivityIcon, Heart, Brain, Zap, Moon } from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { formatDuration } from '../../utils/time';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
);

interface ActivityDetailsProps {
  activity: {
    id: number;
    type: 'workout' | 'sleep' | 'recovery';
    name: string;
    startTime: string;
    endTime: string;
    duration: number;
    metrics?: {
      calories?: number;
      avgHeartRate?: number;
      strain?: number;
      stressLevel?: {
        before: number;
        after: number;
      };
      sleepStages?: {
        rem: number;
        deep: number;
        light: number;
        awake: number;
      };
    };
  };
  onClose: () => void;
}

export function ActivityDetails({ activity, onClose }: ActivityDetailsProps) {
  const generateData = (baseline: number, range: number, days: number) => {
    return Array.from({ length: days }, (_, i) => ({
      x: new Date(Date.now() - (days - i - 1) * 24 * 60 * 60 * 1000),
      y: baseline + (Math.random() - 0.5) * range
    }));
  };

  const heartRateData = generateData(activity.metrics?.avgHeartRate || 120, 20, 30);
  const durationData = generateData(activity.duration, 15, 30);
  const strainData = activity.type === 'workout' ? generateData(activity.metrics?.strain || 10, 4, 30) : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4
      }
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'day' as const,
          displayFormats: {
            day: 'MMM d'
          }
        },
        grid: {
          display: false
        },
        ticks: {
          color: 'hsl(var(--muted-foreground))'
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(var(--muted-foreground), 0.1)'
        },
        ticks: {
          color: 'hsl(var(--muted-foreground))'
        }
      }
    }
  };

  const createChartData = (data: any[], label: string, color: string) => ({
    datasets: [{
      label,
      data: data,
      borderColor: `hsl(var(--${color}))`,
      backgroundColor: `hsl(var(--${color}))`,
      tension: 0.4,
      pointRadius: 2,
      pointHoverRadius: 4
    }]
  });

  // Sleep stages data for bar chart
  const sleepStagesData = activity.type === 'sleep' && activity.metrics?.sleepStages ? {
    labels: ['REM Sleep', 'Deep Sleep', 'Light Sleep', 'Awake'],
    datasets: [
      {
        data: [
          activity.metrics.sleepStages.rem,
          activity.metrics.sleepStages.deep,
          activity.metrics.sleepStages.light,
          activity.metrics.sleepStages.awake,
        ],
        backgroundColor: [
          'hsl(var(--purple-500))',
          'hsl(var(--primary))',
          'hsl(var(--success))',
          'hsl(var(--muted-foreground))',
        ],
      },
    ],
  } : null;

  const sleepStagesOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        title: {
          display: true,
          text: 'Duration (minutes)',
        },
      },
    },
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: true,
        position: 'bottom' as const,
      },
    },
  };

  return (
    <div className="fixed inset-0 bg-background/90 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="theme-card p-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="relative">
                {activity.type === 'workout' && <ActivityIcon className="w-6 h-6 text-primary" />}
                {activity.type === 'sleep' && <Moon className="w-6 h-6 text-purple-500" />}
                {activity.type === 'recovery' && <Heart className="w-6 h-6 text-success" />}
                <div className="absolute inset-0 bg-current opacity-20 blur-xl rounded-full" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{activity.name}</h2>
                <div className="text-sm text-muted-foreground">
                  {activity.startTime} - {activity.endTime} ({formatDuration(activity.duration)})
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="theme-card p-4">
              <div className="text-sm text-muted-foreground mb-1">Duration</div>
              <div className="text-2xl font-bold">{formatDuration(activity.duration)}</div>
              <div className="flex items-center gap-1 text-sm text-success mt-1">
                <TrendingUp className="w-4 h-4" />
                <span>+5% vs last week</span>
              </div>
            </div>

            {activity.metrics?.avgHeartRate && (
              <div className="theme-card p-4">
                <div className="text-sm text-muted-foreground mb-1">Avg Heart Rate</div>
                <div className="text-2xl font-bold">{activity.metrics.avgHeartRate} bpm</div>
                <div className="flex items-center gap-1 text-sm text-destructive mt-1">
                  <TrendingDown className="w-4 h-4" />
                  <span>-3% vs last week</span>
                </div>
              </div>
            )}

            {activity.metrics?.calories && (
              <div className="theme-card p-4">
                <div className="text-sm text-muted-foreground mb-1">Calories</div>
                <div className="text-2xl font-bold">{activity.metrics.calories}</div>
                <div className="flex items-center gap-1 text-sm text-success mt-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>+8% vs last week</span>
                </div>
              </div>
            )}

            {activity.metrics?.stressLevel && (
              <div className="theme-card p-4">
                <div className="text-sm text-muted-foreground mb-1">Stress Level Change</div>
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  <div className="text-2xl font-bold">
                    {activity.metrics.stressLevel.before} → {activity.metrics.stressLevel.after}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm text-success mt-1">
                  <TrendingDown className="w-4 h-4" />
                  <span>-{activity.metrics.stressLevel.before - activity.metrics.stressLevel.after} points</span>
                </div>
              </div>
            )}

            {activity.type === 'sleep' && activity.metrics?.sleepStages && (
              <>
                <div className="theme-card p-4">
                  <div className="text-sm text-muted-foreground mb-1">Deep Sleep</div>
                  <div className="flex items-center gap-2">
                    <Moon className="w-5 h-5 text-primary" />
                    <div className="text-2xl font-bold">
                      {formatDuration(activity.metrics.sleepStages.deep)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-success mt-1">
                    <TrendingUp className="w-4 h-4" />
                    <span>+12% vs average</span>
                  </div>
                </div>
                <div className="theme-card p-4">
                  <div className="text-sm text-muted-foreground mb-1">REM Sleep</div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-500" />
                    <div className="text-2xl font-bold">
                      {formatDuration(activity.metrics.sleepStages.rem)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-success mt-1">
                    <TrendingUp className="w-4 h-4" />
                    <span>+8% vs average</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Sleep Summary */}
          {activity.type === 'sleep' && activity.metrics?.sleepStages && (
            <div className="theme-card p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Sleep Summary</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      Your sleep quality was excellent with {formatDuration(activity.metrics.sleepStages.deep)} of deep sleep
                      and {formatDuration(activity.metrics.sleepStages.rem)} of REM sleep. This indicates good recovery and
                      cognitive restoration.
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Sleep Efficiency</span>
                        <span className="font-medium">
                          {Math.round(((activity.duration - activity.metrics.sleepStages.awake) / activity.duration) * 100)}%
                        </span>
                      </div>
                      <div className="theme-progress-bar h-2">
                        <div
                          className="theme-progress-bar-success"
                          style={{
                            width: `${((activity.duration - activity.metrics.sleepStages.awake) / activity.duration) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-48">
                  <Bar data={sleepStagesData!} options={sleepStagesOptions} />
                </div>
              </div>
            </div>
          )}

          {/* Recovery Summary */}
          {activity.type === 'recovery' && activity.metrics?.stressLevel && (
            <div className="theme-card p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Recovery Impact</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    This recovery session successfully reduced your stress level from {activity.metrics.stressLevel.before} to{' '}
                    {activity.metrics.stressLevel.after}, showing a significant improvement in your recovery state.
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Stress Reduction</span>
                      <span className="font-medium">
                        {Math.round(
                          ((activity.metrics.stressLevel.before - activity.metrics.stressLevel.after) /
                            activity.metrics.stressLevel.before) *
                            100
                        )}%
                      </span>
                    </div>
                    <div className="theme-progress-bar h-2">
                      <div
                        className="theme-progress-bar-success"
                        style={{
                          width: `${
                            ((activity.metrics.stressLevel.before - activity.metrics.stressLevel.after) /
                              activity.metrics.stressLevel.before) *
                            100
                          }%`
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="h-48">
                  <Line
                    data={createChartData(
                      [
                        { x: new Date(Date.now() - 3600000), y: activity.metrics.stressLevel.before },
                        { x: new Date(), y: activity.metrics.stressLevel.after },
                      ],
                      'Stress Level',
                      'primary'
                    )}
                    options={chartOptions}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Trend Graphs */}
          <div className="space-y-8">
            {activity.type === 'workout' && (
              <>
                <div className="theme-card p-6">
                  <h3 className="text-lg font-semibold mb-4">Duration Trend</h3>
                  <div className="h-64">
                    <Line data={createChartData(durationData, 'Duration', 'primary')} options={chartOptions} />
                  </div>
                </div>

                {activity.metrics?.avgHeartRate && (
                  <div className="theme-card p-6">
                    <h3 className="text-lg font-semibold mb-4">Heart Rate Trend</h3>
                    <div className="h-64">
                      <Line data={createChartData(heartRateData, 'Heart Rate', 'destructive')} options={chartOptions} />
                    </div>
                  </div>
                )}

                {strainData && (
                  <div className="theme-card p-6">
                    <h3 className="text-lg font-semibold mb-4">Strain Trend</h3>
                    <div className="h-64">
                      <Line data={createChartData(strainData, 'Strain', 'warning')} options={chartOptions} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}